import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Brain, Mail, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { sendMagicLink, completeMagicLinkSignIn } from '../../firebase/auth';

function MagicLinkAuth() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  useEffect(() => {
    // Check if we're completing a sign-in from the magic link
    const completeSignIn = async () => {
      if (location.search.includes('apiKey') && location.search.includes('oobCode')) {
        setIsCompleting(true);
        try {
          const savedEmail = window.localStorage.getItem('emailForSignIn');
          await completeMagicLinkSignIn(savedEmail);
          navigate('/dashboard');
        } catch (err) {
          setError('Failed to complete sign in. The link may have expired.');
          setIsCompleting(false);
        }
      }
    };

    completeSignIn();
  }, [location, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await sendMagicLink(email);
      setSuccess('Magic link sent! Check your email to sign in.');
      setEmail('');
    } catch (err) {
      setError(err.message || 'Failed to send magic link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (isCompleting) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900 text-white flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <Loader className="w-16 h-16 mx-auto mb-4 animate-spin text-primary-400" />
          <h2 className="text-2xl font-bold mb-2">Completing Sign In...</h2>
          <p className="text-gray-400">Please wait while we verify your email link.</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900 text-white flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-md w-full"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-2 mb-4 cursor-pointer" onClick={() => navigate('/dashboard')}>
            <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold gradient-text">Deeplexity</span>
          </div>
          <h1 className="text-3xl font-bold mb-2">Magic Link Sign In</h1>
          <p className="text-gray-400">We'll send you a secure link to sign in without a password</p>
        </div>

        {/* Magic Link Form */}
        <div className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-start space-x-3"
              >
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-400">{error}</p>
              </motion.div>
            )}

            {success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-green-500/10 border border-green-500/50 rounded-lg p-4 flex items-start space-x-3"
              >
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-green-400 font-medium">{success}</p>
                  <p className="text-xs text-green-400/80 mt-1">
                    The link will expire in 1 hour. Check your spam folder if you don't see it.
                  </p>
                </div>
              </motion.div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full bg-dark-700/50 border border-white/10 rounded-lg pl-11 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-primary-500 to-secondary-500 py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>Sending Magic Link...</span>
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5" />
                  <span>Send Magic Link</span>
                </>
              )}
            </button>
          </form>

          <div className="mt-6 p-4 bg-primary-500/10 border border-primary-500/30 rounded-lg">
            <h3 className="text-sm font-semibold text-primary-400 mb-2">How it works:</h3>
            <ul className="text-xs text-gray-400 space-y-1">
              <li>1. Enter your email address</li>
              <li>2. Check your inbox for the magic link</li>
              <li>3. Click the link to sign in instantly</li>
            </ul>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-400">
              Prefer using a password?{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-primary-400 hover:text-primary-300 font-medium"
              >
                Sign in
              </button>
              {' '}or{' '}
              <button
                onClick={() => navigate('/signup')}
                className="text-primary-400 hover:text-primary-300 font-medium"
              >
                Sign up
              </button>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default MagicLinkAuth;
